<!--
  Can compare 2 objects or 2 array of objects,
  And display both of them with their differences

  Properties:
  - view: the object or array to display
  - compare: the object or array to compare view with
      (and display when different)
  - prop, default, diff: attriubute of the implicit comparable (if any)
  - listeners: a dict of listeners, that will be forwarded to the template
      only when displaying the view (and not the compare)
  - attrs: a dict of attributes, that will be forwarded to the template
      only when displaying the view (and not the compare)
      to be used in v-bind
      to be used in v-on
  - vuedata: an arbitrary object forwarded to the template
      if it is a function, it will be evaluated before passing to each item
  - :prop="...": merged with attrs
  - @event="...": merge with listeners

  Usage:
  <comparator
    :view="value"
    :compare="other">
    <template v-slot="{ value, other, prop, attrs, status }">
      <div>
        <comparable
          prop="field1">
          <template v-slot="{ status }">
            <span :class="'comparable-' + status"
              >{{value.field1}}</span>
          </template>
        </comparator>
        <comparable
          prop="field2">
          <template v-slot="{ status }">
            <span :class="'comparable-' + status"
              >{{value.field2}}</span>
          </template>
        </comparator>
      </div>
    </template>
  </comparator>

  The template recieves:
  - value: the main value (can be view, compare, or an item of them)
  - other: the value compared with (can be compare or an item of it)
  - index: the index of the item (if view is an array)
  - view: if displaying the view
  - compare: if displaying the compare (=!view)
  - listeners: the listeners attribute,
      only when displaying the view (and not the compare)
      to be used in v-on
  - attrs: the attributes attribute,
      only when displaying the view (and not the compare)
      to be used in v-bind
  - vuedata: the vuedata attribute

  Usage with implicit <comparable>:
  <comparator
    :view="value"
    :compare="other"
    prop="field">
    <template v-slot="{ value, other, prop, attrs, status }">
      <div>
        {{value.field}}
      </div>
    </template>
  </comparator>

  The template recieves:
  - value: the main value (can be view, compare, or an item of them)
  - other: the value compared with (can be compare or an item of it)
  - index: the index of the item (if view is an array)
  - status: status of the comparable
      ("same", "diff", "new", "old", "other", "ignore")
  - view: if value is form view
  - view: if displaying the view
  - compare: if displaying the compare (=!view)
  - listeners: the listeners attribute,
      only when displaying the view (and not the compare)
      to be used in v-on
  - attrs: the attributes attribute,
      only when displaying the view (and not the compare)
      to be used in v-bind
  - vuedata: the vuedata attribute
-->

<template>
  <div class="comparator">
    <comparator-vue ref="vues"
      v-for="vue in vues" :key="vue.key"
      :value="vue.value"
      :other="vue.other"
      :index="vue.index"
      :vuekey="vue.key"
      :vuedata="vue.data"
      :compare="forceCompare || vue.compare"
      :hidden="vue.hidden"
      :listeners="computedListeners"
      :attrs="computedAttrs"
      @comparable="v => updateComparable(vue.key, v)">
      <template v-slot="props">
        <comparable v-if="hasProp"
          :prop="prop"
          :default="_default"
          :diff="diff"
          :attrs="computedAttrs"
          :listeners="computedListeners">
          <template v-slot="props">
            <slot
              v-bind="props"
              :value="vue.value"
              :other="vue.other"
              :index="vue.index"
              :vuedata="vue.data"></slot>
          </template>
        </comparable>
        <slot v-else v-bind="props"></slot>
      </template>
    </comparator-vue>
  </div>
</template>

<script>
import deeEqual from 'fast-deep-equal'
import ComparatorVue from './ComparatorVue.vue'
import Comparable from './Comparable.vue'

export default {
  name: 'Comparator',
  components: {
    ComparatorVue,
    Comparable,
  },
  props: {
    view: {},
    compare: {},
    order: Boolean,
    prop: [String, Function, Boolean],
    default: {},
    diff: Boolean,
    listeners: Object,
    attrs: Object,
    vuedata: [Object, Function],
  },
  data () {
    return {
      vues: [], // all the items (both view and compare)
      comparing: {}, // what an item is compared with
    }
  },
  inject: {
    // look for parent <comparator>
    comparator: { default: null },
  },
  computed: {
    /* to avoid error from default keyword */
    _default () { return this.default },
    /* if a prop attr has been defined */
    hasProp () {
      return !!this.prop || this.prop === ''
    },
    /* merges the listeners */
    computedListeners () {
      return {
        ...this.$listeners,
        ...(this.listeners || {}),
      }
    },
    /* merges the attrs */
    computedAttrs () {
      return {
        ...this.$attrs,
        ...(this.attrs || {}),
      }
    },
    /* a copy of view, if it's an array, for change detection */
    viewCopy () {
      if (Array.isArray(this.view)) {
        return this.view.slice()
      } else return this.view
    },
    /* a copy of compare, if it's an array, for change detection */
    compareCopy () {
      if (Array.isArray(this.compare)) {
        return this.compare.slice()
      } else return this.compare
    },
    /* if this comparator is already in the compare template of a comparator */
    forceCompare () {
      return this.comparator && this.comparator.compare || false
    },
  },
  /* init the vues */
  mounted () {
    this.buildVues()
  },
  watch: {
    /* change of the items of view */
    viewCopy (a, b) { this.detectChange(a, b) },
    /* change of the items of compare */
    compareCopy (a, b) { this.detectChange(a, b) },
  },
  methods: {
    /* check that view or compare has really changed */
    detectChange (a, b) {
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return this.buildVues()
        for (let i = 0; i < a.length; i++) {
          if (a[i] !== b[i]) return this.buildVues()
        }
      } else if (a !== b) return this.buildVues()
    },
    /* build the list of items */
    buildVues () {
      // get all the previous vues, for reuse if possible
      let keys = {}
      for (let vue of this.vues) {
        keys[vue.key] = vue
      }
      // the list of new vues
      let vues = []
      // builds a new vue item and add it to vues
      let newVue = (value, compare, index) => {
        /* - value: the item
          - compare: false if from view, true if from compare,
              undefined if there's no compare
          - index if in an array */
        // get the key (ex: view, compare_3, ...)
        // can use value.key if exists
        let key = compare ? 'compare' : 'view'
        if (index !== undefined) {
          if (typeof value === 'object' && 'key' in value) {
            key += '_' + value.key
          } else key += '_' + index
        }
        // reuse the existent vue if possible
        vues.push(key in keys ? {
          ...keys[key],
          value: value,
          index: index,
          compare: compare,
        // else create a new one
        } : {
          key: key,
          value: value,
          index: index,
          other: undefined,
          compare: compare,
          hidden: false,
          data: { ...((typeof this.vuedata === 'function' ? this.vuedata()
            : this.vuedata) || {}) },
        })
      }
      // compare arguments to call newVue with view items
      // undefined if there's nothing to compare with, false else
      let comparing = this.compareCopy === undefined ? undefined : false
      // view and compare are arrays, iterate through them
      if (Array.isArray(this.viewCopy) || Array.isArray(this.compareCopy)) {
        let view = this.viewCopy || [], compare = this.compareCopy || []
        // must be both arrays
        if (!Array.isArray(view) || !Array.isArray(compare)) {
          throw Error('not both arrays')
        }
        // add all items from view and compare
        view.forEach((value, index) => newVue(value, comparing, index))
        compare.forEach((value, index) => newVue(value, true, index))
      // view and compare are objects
      } else {
        // add view and compare
        if (this.viewCopy !== undefined) {
          newVue(this.viewCopy, comparing)
        }
        if (this.compareCopy !== undefined) {
          newVue(this.compareCopy, true)
        }
      }
      // save new vues array
      this.vues = vues
      this.comparing = {}
      // give time for the <comparable> tags to see the updates
      // and update the comparables
      this.$nextTick(this.updateAllComparables)
    },
    /* get the comapring values form all the <comparable> tags
    and compare vues with each other */
    updateAllComparables () {
      // this.$refs.vues is not initialized yet
      if (!this.$refs.vues) return
      // get all the vues by key
      let keys = {}
      for (let vue of this.vues) {
        vue.hidden = false
        vue.other = undefined
        vue.comparable = undefined
        keys[vue.key] = vue
      }
      // get the comparable value from this.$refs.vues
      for (let vue of this.$refs.vues) {
        if (!keys[vue.vuekey]) continue
        keys[vue.vuekey].comparable = vue.comparable
      }
      // all the vues form view
      let views = this.vues.filter(v => !v.compare)
      // all the vues form compare
      let compares = this.vues.filter(v => v.compare)
      // compare vues with each other
      this.comparing = {} // which vue is compared with which one
      for (let view of views) {
        // ignore vues that cannot be compared
        if (view.comparable === undefined) continue
        for (let compare of compares) {
          // ignore vues that cannot be compared
          if (compare.comparable === undefined) continue
          // this vue is already compared, pass
          if (this.comparing[compare.key]) continue
          // those two views have the deep equal comparable
          // save them as comparing, and hide the one from other
          if (deeEqual(view.comparable, compare.comparable)) {
            this.comparing[view.key] = compare.key
            this.comparing[compare.key] = view.key
            compare.hidden = true
            view.other = compare.value
            break
          }
        }
      }
    },
    /* update one specific comparable */
    updateComparable (key, comparable) {
      // get the related vue
      let vue = this.vues.find(v => v.key === key)
      if (!vue) return
      if (comparable !== undefined) vue.comparable = comparable
      else if (vue.comparable !== undefined) comparable = vue.comparable
      else return
      // if that vue was already comparing, clean them both
      let other = this.comparing[key], todo = null
      if (other) {
        let compare = this.vues.find(v => v.key === other)
        // still deep equal comparable, nothing to do
        if (!compare || deeEqual(comparable, compare.comparable)) return
        else {
          delete this.comparing[key]
          delete this.comparing[other]
          vue.hidden = false
          vue.other = undefined
          compare.hidden = false
          compare.other = undefined
          // that othe vue should also be updated
          todo = other
        }
      }
      // find a new comparing vue
      for (let compare of this.vues.filter(v => v.compare === !vue.compare)) {
        // this vue is already comparing, pass
        if (this.comparing[compare.key]) continue
        // those two views have the deep equal comparable
        // save them as comparing, and hide the one from other
        if (deeEqual(comparable, compare.comparable)) {
          this.comparing[compare.key] = key
          this.comparing[key] = compare.key
          if (vue.compare) vue.hidden = true
          else compare.hidden = true
          vue.other = compare.value
          compare.other = vue.value
          break
        }
      }
      // update the cleaned vue
      if (todo) {
        this.updateComparable(todo)
      }
    },
  },
}
</script>
